import { Button, Group, Title, Tooltip } from "@mantine/core";
import { IconMailFast } from "@tabler/icons-react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { zodResolver } from "mantine-form-zod-resolver";
import { Content } from "shared/components/global/Content";
import { SplitButton } from "shared/components/global/SplitButton";
import {
    ComposeFormProvider,
    composeFormStateSchema,
    composeFormStateToDraftInfoInput,
    useComposeForm,
} from "shared/components/message/compose/context";
import { useComposeFormPermissionChecker } from "shared/components/message/compose/useComposeFormPermissionChecker";
import { ComposeForm } from "shared/components/message/ComposeForm";
import { useComposeFormActions } from "shared/components/message/useComposeFormActions";
import { useMessageActions } from "shared/components/message/useMessageActions";
import { features } from "shared/graphql/features";
import { groupsQueryOptions } from "shared/graphql/groups";
import { useDirtyFormBlocker } from "shared/hooks/useDirtyFormBlocker";
import { useGraphqlMutation } from "shared/hooks/useGraphql";
import { fail } from "shared/utils/fns";
import { logger } from "shared/utils/logger";
import { notify } from "shared/utils/notify";
import { graphql } from "src/gql";
import { Cm, MessageSeverity } from "src/gql/graphql";
import { queryClient } from "src/queryClient";
import { z } from "zod";

const Component = () => {
    const group = Route.useLoaderData();
    const hasCmOverrides = features.enabled("HAS_CM_DELIVERY_OVERRIDES");

    const nav = useNavigate();
    const form = useComposeForm({
        validate: zodResolver(composeFormStateSchema),
        initialValues: {
            customizations: {
                sms: { enabled: false, value: "" },
                phone: { enabled: false, value: "" },
            },
            audience: {
                groups: group ? [group] : [],
                topics: [],
                users: [],
                features: [],
                geofilters: [],
            },
            duration: { span: 1440, absolute: null },
            acknowledgable: false,
            escalations: [],
            severity: MessageSeverity.Informational,
            sender: { id: "" },
            body: "",
            richBody: "",
            options: hasCmOverrides ? [{ method: Cm.Email }] : Object.values(Cm).map((it) => ({ method: it })),
            files: [],
            subject: "",
            meta: { mode: "edit" },
        },
    });

    const createDraft = useGraphqlMutation(mutations.createDraft);
    const { openSendModal } = useComposeFormActions(form);

    function handleSaveAsDraft() {
        if (form.validate().hasErrors) return;

        createDraft
            .mutateAsync({ params: composeFormStateToDraftInfoInput.parse(form.getValues()) })
            .then(async ({ createDraft: { subject, id } }) => {
                form.resetDirty();
                notify.show.success({ message: `Saved '${subject}'` });
                window.interop?.draftDidCreate?.(id);
                return nav({ to: "/messages/drafts/$id", params: { id } });
            })
            .catch(logger.error);
    }

    function handleSend() {
        if (form.validate().hasErrors) return;
        const params = composeFormStateToDraftInfoInput.parse(form.getValues());

        openSendModal({
            async getMessage() {
                const result = await createDraft.mutateAsync({ params });
                return result.createDraft;
            },
        });
    }

    const actions = useMessageActions();
    const permissionChecker = useComposeFormPermissionChecker(form);

    useDirtyFormBlocker(form);

    return (
        <Content paper>
            <Content.Heading backable>
                <Title order={3}>New Message</Title>
            </Content.Heading>

            <ComposeFormProvider form={form}>
                <ComposeForm />
            </ComposeFormProvider>

            <Tooltip {...permissionChecker.getTooltipProps()}>
                <Group justify="flex-end" p="xs" w="min-content" wrap="nowrap" ml="auto">
                    <Button
                        variant="default"
                        onClick={handleSaveAsDraft}
                        disabled={permissionChecker.hasErrors}
                        loading={createDraft.isPending || createDraft.isSuccess}
                    >
                        Save as Draft
                    </Button>

                    <SplitButton
                        onClick={handleSend}
                        menu={{
                            "Send Preview": { fn: () => actions.openPreviewModal({ form }) },
                            "Schedule for Later": {
                                fn: async () => {
                                    if (form.validate().hasErrors) return;
                                    await actions.openScheduleDeliveryModal({
                                        isDisabled: () =>
                                            Object.values(form.getValues().audience).every((part) => part.length == 0)
                                                ? "You must specify an audience."
                                                : null,
                                        async getMessage() {
                                            if (form.validate().hasErrors) return fail("Form has errors.");

                                            const draft = await createDraft.mutateAsync({
                                                params: composeFormStateToDraftInfoInput.parse(form.getValues()),
                                            });

                                            return draft.createDraft;
                                        },
                                    });
                                    form.resetDirty();
                                    await nav({ to: "/messages/scheduled" });
                                },
                            },
                        }}
                        disabled={permissionChecker.hasErrors}
                        leftSection={<IconMailFast size={16} />}
                    >
                        Review & Send
                    </SplitButton>
                </Group>
            </Tooltip>
        </Content>
    );
};

const mutations = {
    createDraft: graphql(`
        mutation ComposeCreateDraft($params: DraftInfoInput!) {
            createDraft(params: $params) {
                id
                subject
            }
        }
    `),
    sendMessage: graphql(`
        mutation ComposeSendMesage($id: UUID!, $expireAt: Instant) {
            sendMessage(id: $id, expireAt: $expireAt) {
                id
            }
        }
    `),
    discardDraft: graphql(`
        mutation ComposeSendPanelModalDiscardDraft($id: UUID!) {
            discardMessageDraft(id: $id) {
                id
            }
        }
    `),
};

/** @public */
export const Route = createFileRoute("/_auth/messages/compose")({
    validateSearch: z.object({
        groupId: z.string().uuid().optional().catch(undefined),
    }),
    loaderDeps: ({ search: { groupId } }) => ({ groupId }),
    loader: async ({ deps: { groupId } }) => {
        if (groupId) {
            const { group } = await queryClient.ensureQueryData(groupsQueryOptions.group(groupId));
            return group;
        }
        return null;
    },
    component: Component,
});
